import { useState, useEffect } from 'react';
import logo from '@/assets/images/AURA_logo.png';
import '@/styles/pages/Credits/CreditsPage.scss';
import { Box, CircularProgress, List, ListItem } from '@mui/material';
import BasePaper from '@/components/ui/BasePaper';
import { fetchProjectInfoAPI } from '@/api/projectInfo';
import { useAppContext } from '@/provider/contexts';
import { ProjectInfoResponse } from '@/types/pages/projectInfo';
import { StickyNote2 } from '@mui/icons-material';
import BaseTitle from '@/components/ui/BaseTitle';
import { useTranslation } from 'react-i18next';

const frontendLibraries = [
    {
        name: 'Vite',
        website: 'https://vitejs.dev/',
    },
    {
        name: 'TypeScript',
        website: 'https://www.typescriptlang.org/',
    },
    {
        name: 'React',
        website: 'https://react.dev/',
    },
    {
        name: 'React DOM',
        website: 'https://reactjs.org/docs/react-dom.html',
    },
    {
        name: 'React Router DOM',
        website: 'https://reactrouter.com/',
    },
    {
        name: 'React Pro Sidebar',
        website: 'https://react-pro-sidebar.js.org/',
    },
    {
        name: 'Material UI',
        website: 'https://mui.com/',
    },
    {
        name: 'Material UI Icons',
        website: 'https://mui.com/material-ui/material-icons/',
    },
    {
        name: 'Material UI X-Data-Grid',
        website: 'https://mui.com/x/react-data-grid/',
    },
    {
        name: 'Material UI X-Date-Pickers',
        website: 'https://mui.com/x/react-date-pickers/',
    },
    {
        name: 'Emotion',
        website: 'https://emotion.sh/docs/introduction',
    },
    {
        name: 'Lodash',
        website: 'https://lodash.com/',
    },
    {
        name: 'Day.js',
        website: 'https://day.js.org/',
    },
    {
        name: 'LocalForage',
        website: 'https://localforage.github.io/localForage/',
    },
    {
        name: 'Axios',
        website: 'https://axios-http.com/',
    },
    {
        name: 'i18next',
        website: 'https://www.i18next.com/',
    },
    {
        name: 'react-i18next',
        website: 'https://react.i18next.com/',
    },
    {
        name: 'File Saver',
        website: 'https://github.com/eligrey/FileSaver.js/',
    },
    {
        name: 'ZXing',
        website: 'https://github.com/zxing-js/library',
    },
    {
        name: 'zbar-wasm',
        website: 'https://github.com/undecaf/zbar-wasm',
    },
    {
        name: 'swiper',
        website: 'https://swiperjs.com/',
    },
];
export default function CreditsPage() {
    const [loading, setLoading] = useState(false);
    const [projectInfoData, setProjectInfoData] = useState<ProjectInfoResponse>({} as ProjectInfoResponse);
    const { setNotification } = useAppContext();

    const fetchProjectInfo = async () => {
        setLoading(true);
        const {
            data: { data, isSuccess, message },
        } = await fetchProjectInfoAPI();

        if (isSuccess) {
            data.information?.libraries.push(...frontendLibraries);
            setProjectInfoData(data);
        } else {
            setNotification({ message: message, severity: 'error' });
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchProjectInfo();
    }, []);
    const { t } = useTranslation('common');
    return (
        <>
            <BasePaper className="credits-page-container">
                {loading ? (
                    <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box>
                        <Box className="logo">
                            <img src={logo} alt={t('AURA Logo')} />
                        </Box>
                        <BaseTitle title={t('Credits')} icon={<StickyNote2 />} />
                        <Box className="text">
                            {t('AURA - Version {{version}}', {
                                version: projectInfoData?.information?.version?.aura ?? '',
                            })}
                        </Box>
                        <Box sx={{ mt: 2 }}>
                            {t('Aura Nimbus was made possible by the following technologies and contributors:')}
                        </Box>
                        <Box>
                            <List>
                                {projectInfoData?.information?.libraries.map((library) => (
                                    <ListItem dense key={library.name}>
                                        {library.name} - {library.website}
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    </Box>
                )}
            </BasePaper>
        </>
    );
}
